/**
 * EVENTSFLO-PORTAL-I18N-LANGUAGE-SELECTOR-PHASE1
 *
 * Compact language selector — mirrors the ThemeToggle look-and-feel.
 *
 * Variants:
 *   - compact (default): icon-only button with dropdown, ideal for headers.
 *   - inline: full-width labeled selector for use inside settings cards.
 *
 * Props:
 *   - variant: "compact" | "inline"
 *   - exclude?: string[]  — language codes to hide (e.g. ["ru"] in Client Portal)
 *   - testId?: string     — overrides the data-testid root
 */
import { Globe, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useI18n } from "./I18nProvider";

export function LanguageSelector({
  variant = "compact",
  exclude = [],
  testId,
}) {
  const { language, setLanguage, availableLanguages, t } = useI18n();
  const options = availableLanguages.filter((o) => !exclude.includes(o.code));
  const rootTestId = testId || "language-selector";

  if (variant === "inline") {
    return (
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid={rootTestId}
      >
        {options.map((opt) => {
          const active = opt.code === language;
          return (
            <Button
              key={opt.code}
              variant={active ? "default" : "outline"}
              size="sm"
              onClick={() => setLanguage(opt.code)}
              data-testid={`${rootTestId}-option-${opt.code}`}
              aria-pressed={active}
            >
              {active && <Check className="h-3.5 w-3.5 mr-1.5" />}
              {opt.nativeLabel}
            </Button>
          );
        })}
      </div>
    );
  }

  // compact
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          data-testid={`${rootTestId}-trigger`}
          aria-label={t("common.language")}
          title={t("common.language")}
        >
          <Globe className="h-4 w-4" />
          <span className="sr-only">{t("common.language")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("common.language")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.code}
            onClick={() => setLanguage(opt.code)}
            data-testid={`${rootTestId}-option-${opt.code}`}
            className="flex items-center justify-between"
          >
            <span>{opt.nativeLabel}</span>
            {opt.code === language && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LanguageSelector;
